import React, { useState } from 'react';
import { ContactInput } from '../../services/crm/contactsService';
import { Company } from '../../services/crm/companiesService';

interface EnhancedContactFormProps {
  companies: Company[];
  onSubmit: (contact: ContactInput) => Promise<void>;
  onCancel: () => void;
  loading?: boolean;
  initialData?: Partial<ContactInput>;
}

const LEAD_SOURCE_OPTIONS = [
  'Website',
  'Referral',
  'Social Media',
  'LinkedIn',
  'Trade Show',
  'Conference',
  'Cold Call',
  'Email Campaign',
  'Google Ads',
  'Partner',
  'Customer',
  'Other'
];

const LEAD_STATUS_OPTIONS = [
  'New',
  'Contacted',
  'Qualified',
  'Unqualified',
  'Nurturing',
  'Converted',
  'Lost'
];

const COUNTRY_OPTIONS = [
  'United States',
  'United Kingdom',
  'Canada',
  'Australia',
  'Germany',
  'France',
  'Spain',
  'Italy',
  'Netherlands',
  'Other'
];

const EnhancedContactForm: React.FC<EnhancedContactFormProps> = ({
  companies,
  onSubmit,
  onCancel,
  loading = false,
  initialData = {}
}) => {
  const [formData, setFormData] = useState<Partial<ContactInput>>({
    first_name: '',
    last_name: '',
    email: '',
    mobile_phone: '',
    business_phone: '',
    business_phone_ext: '',
    home_phone: '',
    other_phone: '',
    job_title: '',
    department: '',
    company_id: '',
    lead_source: '',
    lead_status: 'New',
    linkedin_url: '',
    address_street: '',
    address_city: '',
    address_state: '',
    address_postal_code: '',
    address_country: '',
    notes: '',
    ...initialData
  });

  const [expandedSections, setExpandedSections] = useState({
    basic: true,
    phones: true,
    lead: false,
    address: false,
    social: false,
    notes: false
  });

  const toggleSection = (section: keyof typeof expandedSections) => {
    setExpandedSections(prev => ({ ...prev, [section]: !prev[section] }));
  };

  const handleChange = (field: keyof ContactInput, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.first_name || !formData.email) {
      return;
    }
    await onSubmit(formData as ContactInput);
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4 max-h-[80vh] overflow-y-auto">
      {/* Basic Information - Always Expanded */}
      <div className="rounded-lg border border-gray-200 bg-white">
        <button
          type="button"
          onClick={() => toggleSection('basic')}
          className="w-full flex items-center justify-between px-4 py-3 text-left font-semibold text-gray-900 hover:bg-gray-50"
        >
          <span>📋 Basic Information</span>
          <span>{expandedSections.basic ? '−' : '+'}</span>
        </button>
        {expandedSections.basic && (
          <div className="grid gap-4 p-4 md:grid-cols-2">
            <input
              type="text"
              className="rounded-lg border border-gray-200 px-3 py-2 text-sm focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20"
              placeholder="First Name *"
              value={formData.first_name}
              onChange={(e) => handleChange('first_name', e.target.value)}
              required
              disabled={loading}
            />
            <input
              type="text"
              className="rounded-lg border border-gray-200 px-3 py-2 text-sm focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20"
              placeholder="Last Name *"
              value={formData.last_name}
              onChange={(e) => handleChange('last_name', e.target.value)}
              required
              disabled={loading}
            />
            <input
              type="email"
              className="rounded-lg border border-gray-200 px-3 py-2 text-sm focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20 md:col-span-2"
              placeholder="Email *"
              value={formData.email}
              onChange={(e) => handleChange('email', e.target.value)}
              required
              disabled={loading}
            />
            <input
              type="text"
              className="rounded-lg border border-gray-200 px-3 py-2 text-sm focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20"
              placeholder="Job Title"
              value={formData.job_title}
              onChange={(e) => handleChange('job_title', e.target.value)}
              disabled={loading}
            />
            <input
              type="text"
              className="rounded-lg border border-gray-200 px-3 py-2 text-sm focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20"
              placeholder="Department"
              value={formData.department}
              onChange={(e) => handleChange('department', e.target.value)}
              disabled={loading}
            />
            <select
              className="rounded-lg border border-gray-200 px-3 py-2 text-sm focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20 md:col-span-2"
              value={formData.company_id || ''}
              onChange={(e) => handleChange('company_id', e.target.value || null)}
              disabled={loading}
            >
              <option value="">Select Company (optional)</option>
              {companies.map(company => (
                <option key={company.id} value={company.id}>{company.name}</option>
              ))}
            </select>
          </div>
        )}
      </div>

      {/* Phone Numbers */}
      <div className="rounded-lg border border-gray-200 bg-white">
        <button
          type="button"
          onClick={() => toggleSection('phones')}
          className="w-full flex items-center justify-between px-4 py-3 text-left font-semibold text-gray-900 hover:bg-gray-50"
        >
          <span>📱 Phone Numbers</span>
          <span>{expandedSections.phones ? '−' : '+'}</span>
        </button>
        {expandedSections.phones && (
          <div className="grid gap-4 p-4 md:grid-cols-2">
            <div className="md:col-span-2">
              <label className="block text-xs font-medium text-gray-600 mb-1">📱 Mobile Phone (Primary)</label>
              <input
                type="tel"
                className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20"
                placeholder="+1 (555) 123-4567"
                value={formData.mobile_phone}
                onChange={(e) => handleChange('mobile_phone', e.target.value)}
                disabled={loading}
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">☎️ Business Phone</label>
              <input
                type="tel"
                className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20"
                placeholder="+1 (555) 987-6543"
                value={formData.business_phone}
                onChange={(e) => handleChange('business_phone', e.target.value)}
                disabled={loading}
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Extension</label>
              <input
                type="text"
                className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20"
                placeholder="1234"
                value={formData.business_phone_ext}
                onChange={(e) => handleChange('business_phone_ext', e.target.value)}
                disabled={loading}
                maxLength={10}
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">🏠 Home Phone</label>
              <input
                type="tel"
                className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20"
                placeholder="+1 (555) 111-2222"
                value={formData.home_phone}
                onChange={(e) => handleChange('home_phone', e.target.value)}
                disabled={loading}
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">📞 Other Phone</label>
              <input
                type="tel"
                className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20"
                placeholder="+1 (555) 333-4444"
                value={formData.other_phone}
                onChange={(e) => handleChange('other_phone', e.target.value)}
                disabled={loading}
              />
            </div>
          </div>
        )}
      </div>

      {/* Lead Information */}
      <div className="rounded-lg border border-gray-200 bg-white">
        <button
          type="button"
          onClick={() => toggleSection('lead')}
          className="w-full flex items-center justify-between px-4 py-3 text-left font-semibold text-gray-900 hover:bg-gray-50"
        >
          <span>🎯 Lead Information</span>
          <span>{expandedSections.lead ? '−' : '+'}</span>
        </button>
        {expandedSections.lead && (
          <div className="grid gap-4 p-4 md:grid-cols-2">
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Lead Source</label>
              <select
                className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20"
                value={formData.lead_source}
                onChange={(e) => handleChange('lead_source', e.target.value)}
                disabled={loading}
              >
                <option value="">Select source...</option>
                {LEAD_SOURCE_OPTIONS.map(source => (
                  <option key={source} value={source}>{source}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Lead Status</label>
              <select
                className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20"
                value={formData.lead_status}
                onChange={(e) => handleChange('lead_status', e.target.value)}
                disabled={loading}
              >
                {LEAD_STATUS_OPTIONS.map(status => (
                  <option key={status} value={status}>{status}</option>
                ))}
              </select>
            </div>
          </div>
        )}
      </div>

      {/* Address */}
      <div className="rounded-lg border border-gray-200 bg-white">
        <button
          type="button"
          onClick={() => toggleSection('address')}
          className="w-full flex items-center justify-between px-4 py-3 text-left font-semibold text-gray-900 hover:bg-gray-50"
        >
          <span>📍 Address</span>
          <span>{expandedSections.address ? '−' : '+'}</span>
        </button>
        {expandedSections.address && (
          <div className="grid gap-4 p-4 md:grid-cols-2">
            <input
              type="text"
              className="rounded-lg border border-gray-200 px-3 py-2 text-sm focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20 md:col-span-2"
              placeholder="Street Address"
              value={formData.address_street}
              onChange={(e) => handleChange('address_street', e.target.value)}
              disabled={loading}
            />
            <input
              type="text"
              className="rounded-lg border border-gray-200 px-3 py-2 text-sm focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20"
              placeholder="City"
              value={formData.address_city}
              onChange={(e) => handleChange('address_city', e.target.value)}
              disabled={loading}
            />
            <input
              type="text"
              className="rounded-lg border border-gray-200 px-3 py-2 text-sm focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20"
              placeholder="State/Province"
              value={formData.address_state}
              onChange={(e) => handleChange('address_state', e.target.value)}
              disabled={loading}
            />
            <input
              type="text"
              className="rounded-lg border border-gray-200 px-3 py-2 text-sm focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20"
              placeholder="Postal Code"
              value={formData.address_postal_code}
              onChange={(e) => handleChange('address_postal_code', e.target.value)}
              disabled={loading}
            />
            <select
              className="rounded-lg border border-gray-200 px-3 py-2 text-sm focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20"
              value={formData.address_country}
              onChange={(e) => handleChange('address_country', e.target.value)}
              disabled={loading}
            >
              <option value="">Select country...</option>
              {COUNTRY_OPTIONS.map(country => (
                <option key={country} value={country}>{country}</option>
              ))}
            </select>
          </div>
        )}
      </div>

      {/* Social & Web */}
      <div className="rounded-lg border border-gray-200 bg-white">
        <button
          type="button"
          onClick={() => toggleSection('social')}
          className="w-full flex items-center justify-between px-4 py-3 text-left font-semibold text-gray-900 hover:bg-gray-50"
        >
          <span>💼 Social & Web</span>
          <span>{expandedSections.social ? '−' : '+'}</span>
        </button>
        {expandedSections.social && (
          <div className="p-4">
            <input
              type="url"
              className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20"
              placeholder="LinkedIn URL (https://linkedin.com/in/...)"
              value={formData.linkedin_url}
              onChange={(e) => handleChange('linkedin_url', e.target.value)}
              disabled={loading}
            />
          </div>
        )}
      </div>

      {/* Notes */}
      <div className="rounded-lg border border-gray-200 bg-white">
        <button
          type="button"
          onClick={() => toggleSection('notes')}
          className="w-full flex items-center justify-between px-4 py-3 text-left font-semibold text-gray-900 hover:bg-gray-50"
        >
          <span>📝 Internal Notes</span>
          <span>{expandedSections.notes ? '−' : '+'}</span>
        </button>
        {expandedSections.notes && (
          <div className="p-4">
            <textarea
              className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20"
              placeholder="Add internal notes about this contact..."
              value={formData.notes}
              onChange={(e) => handleChange('notes', e.target.value)}
              disabled={loading}
              rows={4}
            />
            <p className="mt-1 text-xs text-gray-500">These notes are only visible to your team</p>
          </div>
        )}
      </div>

      {/* Action Buttons */}
      <div className="flex justify-end gap-3 pt-4 border-t border-gray-200">
        <button
          type="button"
          onClick={onCancel}
          disabled={loading}
          className="rounded-lg border border-gray-300 px-4 py-2 text-sm font-semibold text-gray-700 transition hover:bg-gray-50 disabled:opacity-50"
        >
          Cancel
        </button>
        <button
          type="submit"
          disabled={loading || !formData.first_name || !formData.email}
          className="rounded-lg bg-green-600 px-4 py-2 text-sm font-semibold text-white transition hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {loading ? 'Saving...' : 'Save Contact'}
        </button>
      </div>
    </form>
  );
};

export default EnhancedContactForm;
